<template>
  <c-container
    padding-size="small"
    :style="wrapperStyle"
    class="app-header"
  >
    <div>
      <div
        v-if="page.left?.component === 'logo'"
        :style="{ height: '3.2rem', userSelect: 'none' }"
      >
        <img
          src="/logo.svg"
          :style="{ userSelect: 'none', maxWidth: '10rem' }"
        >
      </div>
      <div v-if="page.left?.component === 'button'">
        <c-button
          :icon="page.left?.icon"
          fab
          color="light"
          @click="page.left?.onclick"
        />
      </div>
    </div>
    <div :style="{ margin: '0 auto' }">
      <div
        v-if="page.center?.component === 'workspace'"
        :style="{ display: 'flex', alignItems: 'center' }"
      >
        <c-avatar
          :user-name="workspaceTitle"
          :src="workspaceIcon"
          size="24px"
          :style="{ marginRight: '.6rem' }"
        />
        <div :style="{ fontWeight: '500' }">
          {{ workspaceTitle }}
        </div>
      </div>
      <div
        v-else-if="page.center?.component === 'text'"
        :style="{ fontWeight: '500' }"
      >
        {{ page.center?.text }}
      </div>
    </div>
    <c-flex-layout
      :style="{ cursor: 'unset' }"
      @mousedown.stop
    >
      <template #left>
        <c-flex-layout>
          <template #left>
            <c-dropdown
              v-if="betaPhaseManager.isInBeta.value && page.right.component === 'user'"
              :style="{ marginRight: '1rem' }"
            >
              <c-tag
                title="BETA"
                color="red"
                :style="{ cursor: 'pointer' }"
              />
              <template #content>
                <c-card
                  padding
                  :style="{ width: '25rem'}"
                >
                  <p>
                    Notix is currently in beta testing phase.
                    In the beta phase you can use the AI features and page blocks for free.
                    After that day, you will have to upgrade
                    your plan to keep using those features.
                  </p>
                  <p
                    v-if="betaPhaseManager.showBetaPhaseCountDown.value"
                    :style="betaPhaseManager.endInDays.value < 10 ? { color: $colors.red.base } : {}"
                  >
                    The beta phase will end in {{ betaPhaseManager.endInDays }} day(s).
                  </p>
                  <c-button
                    href="https://tally.so/r/3NloWW"
                    outlined
                    target="_blank"
                    :style="{ marginTop: '1rem' }"
                  >
                    Give feedback
                  </c-button>
                  <c-button
                    href="https://tally.so/r/w2aPNA"
                    outlined
                    target="_blank"
                    :style="{ marginTop: '1rem' }"
                  >
                    Contact us
                  </c-button>
                </c-card>
              </template>
            </c-dropdown>
          </template>
          <template #right>
            <c-dropdown
              v-if="page.right.component === 'user'"
              :title="userDisplayName"
            >
              <div :style="{ position: 'relative' }">
                <c-avatar
                  :style="{ cursor: 'pointer' }"
                  :user-name="userDisplayName"
                  :src="user.photoURL"
                  size="24px"
                />
                <c-icon
                  v-if="storageUsedInPercent > STORAGE_HINT_LIMIT"
                  type="exclamation-circle-filled"
                  size="16"
                  :color="$colors.red.base"
                  :style="{ position: 'absolute', top: '-.5rem', left: '1.6rem' }"
                />
              </div>
              <template #content>
                <c-card
                  contains-list
                  :style="{ minWidth: '25rem' }"
                >
                  <div :style="{ fontSize: $fontSizes[2], color: $colors.grey.base, padding: '.2rem 1.2rem .6rem' }">
                    {{ user.email }}
                  </div>
                  <c-card-item
                    title="Settings"
                    icon="setting"
                    @click="$emit('navigate', 'settings')"
                  />
                  <c-card-item
                    icon="history"
                    :disabled="isInFreePlan"
                    @click="handleHistoryClick"
                  >
                    <template #title>
                      History <c-tag
                        v-if="isInFreePlan"
                        title="Upgrade needed"
                      />
                    </template>
                  </c-card-item>
                  <c-card-item
                    :title="planTitle"
                    icon="upgrade"
                    :href="upgradeUrl"
                    target="_blank"
                  />
                  <a
                    v-if="storageUsedInPercent > STORAGE_HINT_LIMIT"
                    :href="upgradeUrl"
                    target="_blank"
                  >
                    <c-card-item>
                      <template #title>
                        <storage-hint />
                      </template>
                    </c-card-item>
                  </a>
                  <c-divider size="small" />
                  <a
                    href="https://tally.so/r/3NloWW"
                    target="_blank"
                  >
                    <c-card-item
                      title="Give feedback"
                      icon="message"
                    />
                  </a>
                  <c-divider size="small" />
                  <c-card-item
                    title="Logout"
                    @click="$emit('logout')"
                  />
                </c-card>
              </template>
            </c-dropdown>
          </template>
        </c-flex-layout>
      </template>
    </c-flex-layout>
  </c-container>
</template>

<script>
import CAvatar from 'shared/components/CAvatar.vue';
import CButton from 'shared/components/CButton.vue';
import CCard from 'shared/components/CCard.vue';
import CCardItem from 'shared/components/CCardItem.vue';
import CContainer from 'shared/components/CContainer.vue';
import CDivider from 'shared/components/CDivider.vue';
import CDropdown from 'shared/components/CDropdown.vue';
import CFlexLayout from 'shared/components/CFlexLayout.vue';
import CIcon from 'shared/components/CIcon.vue';
import CTag from 'shared/components/CTag.vue';
import StorageHint from 'shared/components/StorageHint.vue';
import {
  BETA_PHASE_MANAGER,
  plans,
  STORAGE_HINT_LIMIT,
  USER_SERVICE,
} from 'shared/constants';
import useSubscription from 'shared/services/subscription';
import { inject } from 'vue';

import useDebounce from '../services/debounce';

export default {
  name: 'AppHeader',
  props: {
    page: {
      type: Object,
      required: true,
    },
    userDisplayName: {
      type: String,
      default: '',
    },
    user: {
      type: Object,
      default: () => ({}),
    },
    userProfileImage: {
      type: String,
      default: '',
    },
    workspaceTitle: {
      type: String,
      default: '',
    },
    workspaceIcon: {
      type: String,
      default: '',
    },
  },
  components: { StorageHint, CContainer, CIcon, CTag, CDivider, CFlexLayout, CButton, CAvatar, CDropdown, CCard, CCardItem },
  emits: ['navigate', 'logout'],
  data() {
    return {
      hasShadow: false,
      dragging: false,
      STORAGE_HINT_LIMIT,
    };
  },
  setup() {
    const { debounce } = useDebounce();
    const betaPhaseManager = inject(BETA_PHASE_MANAGER);
    const userService = inject(USER_SERVICE);
    if (userService.currentUser.value === null) {
      return { debounce, betaPhaseManager, currentPlan: {} };
    }
    const { currentPlan } = useSubscription(userService.currentUser, betaPhaseManager);
    return { debounce, betaPhaseManager, currentPlan };
  },
  computed: {
    isInFreePlan() {
      return this.currentPlan.id === plans.free.id && !this.betaPhaseManager.isInBeta.value;
    },
    planTitle() {
      if (this.currentPlan.id === plans.free.id) {
        return 'Upgrade';
      }
      return 'Plans';
    },
    upgradeUrl() {
      return `${import.meta.env.VITE_WEBSITE_URL}/app/plans`;
    },
    storageUsedInPercent() {
      return Math.round((this.user.subscriptionData.storageUsed / this.currentPlan.storageLimit) * 100);
    },
    wrapperStyle() {
      if (this.hasShadow) {
        return {
          paddingTop: '.8rem',
          paddingBottom: '.8rem',
          display: 'flex',
          alignItems: 'center',
          boxShadow: 'rgba(38, 50, 56, .1) 0 0 0 1px, rgba(38, 50, 56, .1) 0 2px 4px',
          baseMouseX: 0,
          baseMouseY: 0,
        };
      }
      return {
        paddingTop: '.8rem',
        paddingBottom: '.8rem',
        display: 'flex',
        alignItems: 'center',
      };
    },
  },
  methods: {
    handleHistoryClick() {
      if (this.isInFreePlan) {
        return;
      }

      this.$emit('navigate', 'history');
    },
    handleDragStart(evt) {
      this.baseMouseX = evt.clientX;
      this.baseMouseY = evt.clientY;

      window.parent.postMessage({
        msg: 'NOTIX_DRAG_START',
        mouseX: this.baseMouseX,
        mouseY: this.baseMouseY,
      }, '*');

      document.addEventListener('mouseup', this.handleDragEnd);
      document.addEventListener('mousemove', this.handleMousemove);
    },

    handleMousemove(evt) {
      const emit = () => {
        window.parent.postMessage({
          msg: 'NOTIX_DRAG_MOUSEMOVE',
          offsetX: evt.clientX - this.baseMouseX,
          offsetY: evt.clientY - this.baseMouseY,
        }, '*');
      };
      this.debounce(emit, 1);
    },

    handleDragEnd() {
      window.parent.postMessage({ msg: 'NOTIX_DRAG_END' }, '*');

      document.removeEventListener('mouseup', this.handleDragEnd);
      document.removeEventListener('mousemove', this.handleMousemove);
    },
    register() {
      const floatHeaderDistanceTop = 20;
      const setShadow = (value) => {
        this.hasShadow = value;
      };

      const container = document.getElementsByClassName('c-container')[1];
      if (container === undefined) {
        setShadow(false);
        return;
      }

      container.addEventListener('scroll', () => {
        if (container.scrollTop > floatHeaderDistanceTop) {
          setShadow(true);
          return;
        }
        if (container.scrollTop > floatHeaderDistanceTop) {
          return;
        }
        setShadow(false);
      });
    },
  },
  watch: {
    page: {
      handler() {
        this.$nextTick(() => {
          this.register();
        });
      },
      deep: true,
    },
  },
  created() {
    this.register();
  },
};
</script>

<style scoped lang="scss" type="text/scss">
  .app-header {
    -webkit-app-region: drag;
    position: relative;
  }
</style>
